import type {
  ConflictDocument,
  DocumentsFormData,
  DocumentsValidationResult,
  ProcessoArbitralDraftDocumentItem,
  ProcessoArbitralDraftUploadItem,
  ProofDocumentRequirement,
  ProcessoArbitralDraftDocumentUploadStatus,
  RemovedDraftDocumentIds,
  RequiredProofDocumentType,
} from '@/features/processo-arbitral/types/documentsTypes';
import type { FileUploadStatus } from '@/shared/types/arquivosUpload';
import type { PartyRepresentedSide } from '@/features/processo-arbitral/types/contactTypes';
import type { RequerentePartyItem } from '@/features/processo-arbitral/types/requerenteStepTypes';
import type { RequeridoPartyItem } from '@/features/processo-arbitral/types/requeridoStepTypes';
import type { PartyRole } from '@/features/processo-arbitral/types/contactTypes';

export const acceptedDocumentExtensions = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.doc',
  '.docx',
] as const;
export const acceptedDocumentExtensionsText = 'PDF, JPG, PNG, DOC ou DOCX';
export const maxDocumentFileSize = 50 * 1024 * 1024;
export const maxDocumentFileSizeLabel = `${maxDocumentFileSize / (1024 * 1024)} MB`;

const compressedDocumentExtensions = ['.zip', '.rar', '.7z', '.tar', '.gz'];

const proofDocumentMetadata: Record<
  RequiredProofDocumentType,
  {
    label: string;
    articleLabel: string;
  }
> = {
  procuracao: {
    label: 'Procuração assinada',
    articleLabel: 'procuração',
  },
  carteiraOab: {
    label: 'Carteira da OAB',
    articleLabel: 'carteira da OAB',
  },
  contratoSocial: {
    label: 'Contrato social',
    articleLabel: 'contrato social',
  },
};

export const defaultDocumentsFormData: DocumentsFormData = {
  comprovacao: [],
  conflito: [],
};

const getProofDocumentTypesByRole = (
  role: 'advogado' | 'representante',
): RequiredProofDocumentType[] => {
  if (role === 'advogado') {
    return ['procuracao', 'carteiraOab'];
  }

  return ['contratoSocial', 'procuracao'];
};

const buildProofRequirementId = (
  representedSide: PartyRepresentedSide,
  partyRole: 'advogado' | 'representante',
  partyId: string,
  documentType: RequiredProofDocumentType,
): string => `${representedSide}-${partyRole}-${partyId}-${documentType}`;

const isProofPartyRole = (role: PartyRole): role is 'advogado' | 'representante' =>
  role === 'advogado' || role === 'representante';

const isProofParty = (
  party: RequerentePartyItem | RequeridoPartyItem,
): party is (RequerentePartyItem | RequeridoPartyItem) & {
  role: 'advogado' | 'representante';
} => isProofPartyRole(party.role);

const mergeExistingProofDocument = (
  requirement: ProofDocumentRequirement,
  existing?: ProofDocumentRequirement,
): ProofDocumentRequirement => {
  if (!existing) {
    return requirement;
  }

  return {
    ...requirement,
    uploadId: existing.uploadId,
    fileId: existing.fileId,
    fileName: existing.fileName,
    fileSize: existing.fileSize,
    mimeType: existing.mimeType,
    uploadProgress: existing.uploadProgress,
    status: existing.status,
    error: existing.error,
    uploadedAt: existing.uploadedAt,
    isRemoving: existing.isRemoving,
  };
};

const getProofDocumentDisplayLabel = (documentType: RequiredProofDocumentType): string =>
  proofDocumentMetadata[documentType].label;

const getProofDocumentErrorLabel = (documentType: RequiredProofDocumentType): string =>
  proofDocumentMetadata[documentType].articleLabel;

const getProofDocumentTypeFromSubtipo = (
  subtipo:
    | ProcessoArbitralDraftDocumentItem['subtipo']
    | ProcessoArbitralDraftUploadItem['subtipo'],
): RequiredProofDocumentType | null => {
  if (subtipo === 'PROCURACAO') {
    return 'procuracao';
  }

  if (subtipo === 'CARTEIRA_OAB') {
    return 'carteiraOab';
  }

  if (subtipo === 'CONTRATO_SOCIAL') {
    return 'contratoSocial';
  }

  return null;
};

const mapUploadStatusToFileStatus = (
  status: ProcessoArbitralDraftDocumentUploadStatus,
): Exclude<ProofDocumentRequirement['status'], 'idle' | 'cancelled'> => {
  if (status === 'PROCESSADO') {
    return 'done';
  }

  if (status === 'ERRO') {
    return 'error';
  }

  return 'processing';
};

const buildProofRequirementFromBackend = (input: {
  id: string;
  partyId: string | null;
  partyName: string | null;
  partyRole: string | null;
  representedSide: PartyRepresentedSide | null;
  subtipo:
    | ProcessoArbitralDraftDocumentItem['subtipo']
    | ProcessoArbitralDraftUploadItem['subtipo'];
}): ProofDocumentRequirement | null => {
  const documentType = getProofDocumentTypeFromSubtipo(input.subtipo);

  if (
    !documentType ||
    (input.partyRole !== 'advogado' && input.partyRole !== 'representante') ||
    !input.partyId ||
    !input.partyName ||
    !input.representedSide
  ) {
    return null;
  }

  const representedSideLabel =
    input.representedSide === 'requerente' ? 'parte requerente' : 'parte requerida';

  return {
    id: input.id,
    partyId: input.partyId,
    partyName: input.partyName,
    partyRole: input.partyRole,
    representedSide: input.representedSide,
    documentType,
    documentLabel: getProofDocumentDisplayLabel(documentType),
    requirementLabel: `${getProofDocumentDisplayLabel(documentType)} de ${input.partyName} (${representedSideLabel})`,
    uploadId: null,
    fileId: null,
    fileName: null,
    fileSize: undefined,
    mimeType: undefined,
    uploadProgress: 0,
    status: 'idle',
    error: undefined,
    uploadedAt: null,
  };
};

const buildConflictDocumentFromBackend = (
  id: string,
  existing?: ConflictDocument,
  subtipo?: ProcessoArbitralDraftDocumentItem['subtipo'],
): ConflictDocument => ({
  id,
  subtipo: subtipo === 'PETICAO_INICIAL' ? 'PETICAO_INICIAL' : existing?.subtipo,
  uploadId: existing?.uploadId ?? null,
  fileId: existing?.fileId ?? null,
  fileName: existing?.fileName ?? '',
  fileSize: existing?.fileSize ?? 0,
  mimeType: existing?.mimeType,
  status: existing?.status ?? 'idle',
  uploadProgress: existing?.uploadProgress ?? 0,
  error: existing?.error,
  uploadedAt: existing?.uploadedAt ?? null,
});

const getUploadCorrelationId = (upload: ProcessoArbitralDraftUploadItem): string =>
  upload.requirementId || upload.documentoId || upload.id;

/**
 * O item que ainda está subindo neste browser não é rebaixado pelo backend.
 *
 * Com o upload direto, o registro nasce na autorização (`upload-intent`) — ANTES
 * da transferência —, então o polling de 1s já o vê como PENDENTE enquanto o PUT
 * corre. Sem esta guarda, a resposta trocava o `uploading` local por
 * `processing` e o progresso por 100: a barra de percentual e o botão Cancelar
 * sumiam com o arquivo ainda no meio do caminho.
 */
const preservarEnvioLocal = <T extends { status: FileUploadStatus }>(
  vindoDoBackend: T,
  local: T | undefined,
): T => (local?.status === 'uploading' ? { ...vindoDoBackend, ...local } : vindoDoBackend);

const upsertProofRequirement = (
  proofMap: Map<string, ProofDocumentRequirement>,
  order: string[],
  requirement: ProofDocumentRequirement,
) => {
  if (!proofMap.has(requirement.id)) {
    order.push(requirement.id);
  }

  proofMap.set(requirement.id, preservarEnvioLocal(requirement, proofMap.get(requirement.id)));
};

const upsertConflictDocument = (
  conflictMap: Map<string, ConflictDocument>,
  order: string[],
  document: ConflictDocument,
) => {
  if (!conflictMap.has(document.id)) {
    order.push(document.id);
  }

  conflictMap.set(document.id, preservarEnvioLocal(document, conflictMap.get(document.id)));
};

export const deriveProofDocumentRequirements = (
  requerentes: RequerentePartyItem[],
  requeridos: RequeridoPartyItem[],
  existing: ProofDocumentRequirement[] = [],
): ProofDocumentRequirement[] => {
  const existingById = new Map(existing.map((item) => [item.id, item]));
  const uniqueProofParties = new Map<
    string,
    {
      representedSide: PartyRepresentedSide;
      item: (RequerentePartyItem | RequeridoPartyItem) & { role: 'advogado' | 'representante' };
    }
  >();

  requerentes.filter(isProofParty).forEach((item) => {
    uniqueProofParties.set(`requerente-${item.role}-${item.party.cpfCnpj}`, {
      representedSide: 'requerente',
      item,
    });
  });

  requeridos.filter(isProofParty).forEach((item) => {
    uniqueProofParties.set(`requerido-${item.role}-${item.party.cpfCnpj}`, {
      representedSide: 'requerido',
      item,
    });
  });

  return Array.from(uniqueProofParties.values()).flatMap(({ representedSide, item }) =>
    getProofDocumentTypesByRole(item.role).map((documentType) => {
      const id = buildProofRequirementId(
        representedSide,
        item.role,
        item.party.cpfCnpj,
        documentType,
      );
      const representedSideLabel =
        representedSide === 'requerente' ? 'parte requerente' : 'parte requerida';
      const requirement = {
        id,
        partyId: item.party.cpfCnpj,
        partyName: item.party.nome,
        partyRole: item.role,
        representedSide,
        documentType,
        documentLabel: getProofDocumentDisplayLabel(documentType),
        requirementLabel: `${getProofDocumentDisplayLabel(documentType)} de ${item.party.nome} (${representedSideLabel})`,
        uploadId: null,
        fileId: null,
        fileName: null,
        fileSize: undefined,
        mimeType: undefined,
        uploadProgress: 0,
        status: 'idle',
        error: undefined,
        uploadedAt: null,
      } satisfies ProofDocumentRequirement;

      return mergeExistingProofDocument(requirement, existingById.get(id));
    }),
  );
};

export const syncDocumentsFormDataWithBackend = (
  existing: DocumentsFormData,
  documents: ProcessoArbitralDraftDocumentItem[],
  uploads: ProcessoArbitralDraftUploadItem[],
): DocumentsFormData => {
  const proofOrder = existing.comprovacao.map((item) => item.id);
  const proofMap = new Map(existing.comprovacao.map((item) => [item.id, item]));
  const conflictOrder = existing.conflito.map((item) => item.id);
  const conflictMap = new Map(existing.conflito.map((item) => [item.id, item]));
  const latestUploads = new Map<string, ProcessoArbitralDraftUploadItem>();

  uploads.forEach((upload) => {
    const correlationId = getUploadCorrelationId(upload);

    if (!latestUploads.has(correlationId)) {
      latestUploads.set(correlationId, upload);
    }
  });

  documents.forEach((document) => {
    if (document.categoria === 'comprovacao') {
      const requirementId = document.requirementId || document.id;
      const existingRequirement = proofMap.get(requirementId);
      const baseRequirement =
        existingRequirement ||
        buildProofRequirementFromBackend({
          id: requirementId,
          partyId: document.partyId,
          partyName: document.partyName,
          partyRole: document.partyRole,
          representedSide: document.representedSide,
          subtipo: document.subtipo,
        });

      if (!baseRequirement) {
        return;
      }

      upsertProofRequirement(proofMap, proofOrder, {
        ...baseRequirement,
        uploadId: existingRequirement?.uploadId ?? null,
        fileId: document.id,
        fileName: document.nomeOriginal || document.nomeArquivo,
        fileSize: document.tamanhoArquivo,
        mimeType: document.tipoMime,
        uploadProgress: 100,
        status: 'done',
        error: undefined,
        uploadedAt: document.uploadedAt,
      });
      return;
    }

    const conflictId = document.requirementId || document.id;
    const existingDocument = conflictMap.get(conflictId);

    upsertConflictDocument(conflictMap, conflictOrder, {
      ...buildConflictDocumentFromBackend(conflictId, existingDocument, document.subtipo),
      fileId: document.id,
      fileName: document.nomeOriginal || document.nomeArquivo,
      fileSize: document.tamanhoArquivo,
      mimeType: document.tipoMime,
      status: 'done',
      uploadProgress: 100,
      error: undefined,
      uploadedAt: document.uploadedAt,
    });
  });

  latestUploads.forEach((upload) => {
    if (upload.categoria === 'comprovacao') {
      const requirementId = getUploadCorrelationId(upload);
      const existingRequirement = proofMap.get(requirementId);
      const baseRequirement =
        existingRequirement ||
        buildProofRequirementFromBackend({
          id: requirementId,
          partyId: upload.partyId,
          partyName: upload.partyName,
          partyRole: upload.partyRole,
          representedSide: upload.representedSide,
          subtipo: upload.subtipo,
        });

      if (!baseRequirement) {
        return;
      }

      upsertProofRequirement(proofMap, proofOrder, {
        ...baseRequirement,
        uploadId: upload.id,
        fileId:
          upload.status === 'PROCESSADO'
            ? (upload.documentoId ?? existingRequirement?.fileId ?? null)
            : (existingRequirement?.fileId ?? null),
        fileName: upload.nomeOriginal || upload.nomeArquivo,
        fileSize: upload.tamanhoArquivo,
        mimeType: upload.tipoMime,
        uploadProgress:
          upload.status === 'ERRO'
            ? 0
            : upload.status === 'PROCESSADO'
              ? 100
              : Math.max(existingRequirement?.uploadProgress ?? 0, 100),
        status: mapUploadStatusToFileStatus(upload.status),
        error: upload.mensagemErro ?? undefined,
        uploadedAt:
          upload.status === 'PROCESSADO'
            ? (upload.processedAt ?? existingRequirement?.uploadedAt ?? null)
            : (existingRequirement?.uploadedAt ?? null),
      });
      return;
    }

    const conflictId = getUploadCorrelationId(upload);
    const existingDocument = conflictMap.get(conflictId);

    upsertConflictDocument(conflictMap, conflictOrder, {
      ...buildConflictDocumentFromBackend(conflictId, existingDocument, upload.subtipo),
      uploadId: upload.id,
      fileId:
        upload.status === 'PROCESSADO'
          ? (upload.documentoId ?? existingDocument?.fileId ?? null)
          : (existingDocument?.fileId ?? null),
      fileName: upload.nomeOriginal || upload.nomeArquivo,
      fileSize: upload.tamanhoArquivo,
      mimeType: upload.tipoMime,
      status: mapUploadStatusToFileStatus(upload.status),
      uploadProgress:
        upload.status === 'ERRO'
          ? 0
          : upload.status === 'PROCESSADO'
            ? 100
            : Math.max(existingDocument?.uploadProgress ?? 0, 100),
      error: upload.mensagemErro ?? undefined,
      uploadedAt:
        upload.status === 'PROCESSADO'
          ? (upload.processedAt ?? existingDocument?.uploadedAt ?? null)
          : (existingDocument?.uploadedAt ?? null),
    });
  });

  return {
    comprovacao: proofOrder
      .map((id) => proofMap.get(id))
      .filter((item): item is ProofDocumentRequirement => Boolean(item)),
    conflito: conflictOrder
      .map((id) => conflictMap.get(id))
      .filter((item): item is ConflictDocument => Boolean(item)),
  };
};

/**
 * Descarta do que o backend devolveu o que o usuário acabou de remover.
 *
 * A listagem do rascunho é buscada em polling: uma resposta em voo quando a
 * remoção foi confirmada traz o anexo de volta e ele reaparece na lista. Os ids
 * removidos ficam registrados no slice e são filtrados aqui — inclusive o upload
 * que aponta para o documento removido, que sozinho já recriava o item.
 *
 * O `requirementId` é o id que o wizard gerou e mandou junto no upload: é a única
 * chave disponível quando o usuário cancela antes de o backend devolver o id do
 * upload, e sem ela o anexo cancelado voltava no primeiro polling.
 */
export const filterRemovedDraftDocuments = (
  documents: ProcessoArbitralDraftDocumentItem[],
  uploads: ProcessoArbitralDraftUploadItem[],
  removed: RemovedDraftDocumentIds,
): {
  documents: ProcessoArbitralDraftDocumentItem[];
  uploads: ProcessoArbitralDraftUploadItem[];
} => ({
  documents: documents.filter(
    (document) =>
      !removed.fileIds.includes(document.id) &&
      !(document.requirementId && removed.requirementIds.includes(document.requirementId)),
  ),
  uploads: uploads.filter(
    (upload) =>
      !removed.uploadIds.includes(upload.id) &&
      !(upload.documentoId && removed.fileIds.includes(upload.documentoId)) &&
      !(upload.requirementId && removed.requirementIds.includes(upload.requirementId)),
  ),
});

export const validateDocumentFile = (file: File): string | null => {
  const fileName = file.name.toLowerCase();
  const extension = `.${fileName.split('.').pop() ?? ''}`;
  const isAcceptedExtension = acceptedDocumentExtensions.includes(
    extension as (typeof acceptedDocumentExtensions)[number],
  );

  if (compressedDocumentExtensions.includes(extension)) {
    return 'Arquivos compactados não são aceitos. Envie cada documento em PDF, JPG, PNG, DOC ou DOCX.';
  }

  if (!isAcceptedExtension) {
    return 'Formato não suportado. Use apenas PDF, JPG, PNG, DOC ou DOCX.';
  }

  if (file.size > maxDocumentFileSize) {
    return `Arquivo muito grande. O tamanho máximo é ${maxDocumentFileSizeLabel} — divida o documento em partes menores.`;
  }

  return null;
};

const isPeticaoInicial = (document: ConflictDocument): boolean =>
  document.subtipo === 'PETICAO_INICIAL';

const isCompletedDocument = (document: ConflictDocument): boolean =>
  document.status === 'done' && Boolean(document.fileId);

const getCompletedConflictDocuments = (documents: ConflictDocument[]): ConflictDocument[] =>
  documents.filter((document) => isCompletedDocument(document) && !isPeticaoInicial(document));

const hasPeticaoInicial = (documents: ConflictDocument[]): boolean =>
  documents.filter((document) => isCompletedDocument(document) && isPeticaoInicial(document))
    .length === 1;

const hasUploadingDocuments = (formData: DocumentsFormData): boolean => {
  return (
    formData.comprovacao.some(
      (document) => document.status === 'uploading' || document.status === 'processing',
    ) ||
    formData.conflito.some(
      (document) => document.status === 'uploading' || document.status === 'processing',
    )
  );
};

export const validateDocumentsFormData = (
  formData: DocumentsFormData,
): DocumentsValidationResult => {
  const errors = formData.comprovacao.reduce<Record<string, string>>((acc, document) => {
    if (document.status === 'done' && document.fileId) {
      return acc;
    }

    acc[document.id] =
      `Faça o upload da ${getProofDocumentErrorLabel(document.documentType)} de ${document.partyName} (${document.representedSide === 'requerente' ? 'parte requerente' : 'parte requerida'})`;
    return acc;
  }, {});

  const hasConflictDocuments = getCompletedConflictDocuments(formData.conflito).length > 0;
  const hasPeticao = hasPeticaoInicial(formData.conflito);

  if (!hasPeticao) {
    errors['peticao-required'] = 'Adicione a petição inicial para continuar.';
  }

  if (!hasConflictDocuments) {
    errors['conflito-required'] = 'Adicione pelo menos 1 documento do conflito para continuar.';
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
    hasUploading: hasUploadingDocuments(formData),
    hasConflictDocuments,
    hasPeticaoInicial: hasPeticao,
    conflictRecommendation: hasConflictDocuments
      ? undefined
      : 'Adicione pelo menos 1 documento do conflito (contrato, comprovante, etc.).',
  };
};

export const createConflictDocumentId = (): string =>
  `conflito-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
